/**
 * Data-driven VFX orchestration layer. Reads vfx-mappings.json to map skill
 * activations/deactivations to VFX primitive sequences. Listens to player
 * events — skill logic remains Phaser-free and testable.
 */
import type Phaser from 'phaser';
import { VfxFactory } from './VfxFactory';
import { VfxConfigLoader } from './VfxConfigLoader';
import mappingsData from '../data/vfx-mappings.json';

/** A single VFX step within a skill's mapping entry. */
export interface VfxStep {
  /** Effect key in vfx.json (e.g. "fire_lighter_contact"). */
  effectKey: string;
  /** When this step triggers relative to the skill event. */
  trigger: 'activation' | 'deactivation' | 'hit' | 'tick' | 'expiration';
  /** Delay in ms after the trigger event before playing (default 0). */
  delayMs?: number;
  /**
   * For externally-triggered (hit/tick/expiration) sustain VFX: auto-stop
   * after this many ms. Overrides the default per-primitive duration.
   */
  sustainDurationMs?: number;
}

/** A complete mapping entry for one skill node. */
export interface VfxMapping {
  /** Skill node ID (must match a node in src/data/trees/*.json). */
  skillId: string;
  /** Ordered list of VFX steps. */
  steps: VfxStep[];
  /**
   * For instant skills with sustain VFX: auto-stop all activation VFX
   * after this many ms. Fires expiration-trigger steps (if any) on cleanup.
   * Not needed for toggle/phased skills — those use the deactivation signal.
   */
  sustainDurationMs?: number;
}

/** Shape of vfx-mappings.json. */
interface VfxMappingsData {
  mappings: VfxMapping[];
}

/** Minimal factory interface for VFX primitive creation — injectable for testing. */
export interface IVfxFactory {
  get(key: string): {
    play(x: number, y: number, depth: number): void;
    stop(): void;
    setDirection(angle: number): void;
    updatePosition(x: number, y: number, depth: number, delta: number): void;
  };
  destroyAll(): void;
}

/**
 * Sustain primitives that loop indefinitely (repeat: -1) and require explicit
 * stop() calls. Any of these spawned by an external trigger (hit/tick/expiration)
 * gets auto-stopped after a default duration unless the step overrides it.
 */
const SUSTAIN_PRIMITIVES: Set<string> = new Set([
  'AuraRing',
  'ZoneOverlay',
  'ChannelBeam',
  'OrbitalSprites',
  'ChainTether',
  'SpiralVortex',
]);

/** Default auto-stop durations (ms) per sustain primitive type for external triggers. */
const DEFAULT_TRIGGER_SUSTAIN_MS: Record<string, number> = {
  AuraRing: 2500,
  ZoneOverlay: 3500,
  ChannelBeam: 2000,
  OrbitalSprites: 3000,
  ChainTether: 2500,
  SpiralVortex: 3000,
};

export class SkillVFXOrchestrator {
  private factory: IVfxFactory;
  private scene: Phaser.Scene;
  private mappingsBySkill: Map<string, VfxStep[]> = new Map();
  /** Per-skill sustainDurationMs from vfx-mappings.json (instant skills only). */
  private sustainDurations: Map<string, number> = new Map();
  private playerEvents: Phaser.Events.EventEmitter | null = null;
  private getPlayerPosition: (() => { x: number; y: number; depth: number }) | null = null;
  private getPlayerFacing: (() => number) | null = null;
  private onActivated: ((data: { skillId: string }) => void) | null = null;
  private onDeactivated: ((data: { skillId: string }) => void) | null = null;
  /** Effect keys whose primitives are currently playing and need per-frame repositioning. */
  private activeSustainKeys: Set<string> = new Set();
  /** Scheduled auto-stop timers for instant skills with sustainDurationMs. */
  private sustainTimers: Map<string, Phaser.Time.TimerEvent> = new Map();
  /** Per-effect-key auto-stop timers for externally-triggered sustain VFX. */
  private triggerSustainTimers: Map<string, Phaser.Time.TimerEvent> = new Map();
  /** Pending delayed VFX calls per skill, cancelled on deactivation to prevent orphaned effects. */
  private pendingDelayedCalls: Map<string, Phaser.Time.TimerEvent[]> = new Map();

  constructor(scene: Phaser.Scene, factory?: IVfxFactory) {
    this.scene = scene;
    this.factory = factory ?? new VfxFactory(scene);
    this.loadMappings();
  }

  /** Load and index mappings from JSON data. */
  private loadMappings(): void {
    VfxConfigLoader.load();
    const data = mappingsData as VfxMappingsData;
    for (const mapping of data.mappings) {
      this.mappingsBySkill.set(mapping.skillId, mapping.steps);
      if (mapping.sustainDurationMs != null && mapping.sustainDurationMs > 0) {
        this.sustainDurations.set(mapping.skillId, mapping.sustainDurationMs);
      }
    }
  }

  /**
   * Wire to player events. Call once after the Player is created in GameScene.
   * @param playerEvents - The player's event emitter (player.events)
   * @param getPosition - Returns current player position + depth for VFX placement
   * @param getFacing - Returns current player facing angle in radians (0 = right)
   */
  wire(
    playerEvents: Phaser.Events.EventEmitter,
    getPosition: () => { x: number; y: number; depth: number },
    getFacing?: () => number,
  ): void {
    this.playerEvents = playerEvents;
    this.getPlayerPosition = getPosition;
    this.getPlayerFacing = getFacing ?? null;

    this.onActivated = (data: { skillId: string }) => {
      this.fireSteps(data.skillId, 'activation');
      this.scheduleSustainCleanup(data.skillId);
    };
    this.onDeactivated = (data: { skillId: string }) => {
      this.cancelSustainTimer(data.skillId);
      this.stopAll(data.skillId);
      this.fireSteps(data.skillId, 'deactivation');
    };

    playerEvents.on('skill-activated', this.onActivated);
    playerEvents.on('skill-deactivated', this.onDeactivated);
  }

  /** Fire all VFX steps matching the given trigger for a skill. */
  private fireSteps(skillId: string, trigger: VfxStep['trigger']): void {
    const steps = this.mappingsBySkill.get(skillId);
    if (!steps) return;

    const pos = this.getPlayerPosition?.();
    if (!pos) return;
    const facing = this.getPlayerFacing?.();

    for (const step of steps) {
      if (step.trigger !== trigger) continue;
      if (!VfxConfigLoader.has(step.effectKey)) continue;

      const delay = step.delayMs ?? 0;
      if (delay > 0) {
        const timer = this.scene.time.delayedCall(delay, () => {
          this.removeDelayedCall(skillId, timer);
          this.playEffect(step.effectKey, pos.x, pos.y, pos.depth, facing);
        });
        this.addDelayedCall(skillId, timer);
      } else {
        this.playEffect(step.effectKey, pos.x, pos.y, pos.depth, facing);
      }
    }
  }

  /** Play a single effect by its vfx.json key. */
  private playEffect(effectKey: string, x: number, y: number, depth: number, facing?: number): void {
    const primitive = this.factory.get(effectKey);
    if (facing != null) primitive.setDirection(facing);
    primitive.play(x, y, depth);
    this.activeSustainKeys.add(effectKey);
  }

  /**
   * Fire VFX steps for a specific skill + trigger from external callers
   * (e.g. hit events from CombatHitResolver, tick events from systems).
   * Sustain primitives are auto-stopped after a default or per-step duration.
   */
  trigger(skillId: string, trigger: VfxStep['trigger'], x: number, y: number, depth: number): void {
    const steps = this.mappingsBySkill.get(skillId);
    if (!steps) return;
    const facing = this.getPlayerFacing?.();

    for (const step of steps) {
      if (step.trigger !== trigger) continue;
      if (!VfxConfigLoader.has(step.effectKey)) continue;

      const delay = step.delayMs ?? 0;
      if (delay > 0) {
        const timer = this.scene.time.delayedCall(delay, () => {
          this.removeDelayedCall(skillId, timer);
          this.playEffect(step.effectKey, x, y, depth, facing);
          this.scheduleTriggerSustainCleanup(step);
        });
        this.addDelayedCall(skillId, timer);
      } else {
        this.playEffect(step.effectKey, x, y, depth, facing);
        this.scheduleTriggerSustainCleanup(step);
      }
    }
  }

  /** Stop all active effects for a skill (e.g. on deactivation). */
  stopAll(skillId: string): void {
    this.cancelDelayedCalls(skillId);
    const steps = this.mappingsBySkill.get(skillId);
    if (!steps) return;

    for (const step of steps) {
      if (!VfxConfigLoader.has(step.effectKey)) continue;
      const primitive = this.factory.get(step.effectKey);
      primitive.stop();
      this.activeSustainKeys.delete(step.effectKey);
      // Cancel any pending trigger sustain timer for this effect
      this.cancelTriggerSustainTimer(step.effectKey);
    }
  }

  /** Reposition all active sustain effects to the current player position. Call every frame. */
  update(delta: number): void {
    if (this.activeSustainKeys.size === 0 || !this.getPlayerPosition) return;
    const { x, y, depth } = this.getPlayerPosition();
    for (const key of this.activeSustainKeys) {
      this.factory.get(key).updatePosition(x, y, depth, delta);
    }
  }

  /** Check if a skill has any VFX mappings. */
  has(skillId: string): boolean {
    return this.mappingsBySkill.has(skillId);
  }

  /**
   * Schedule auto-stop for instant skills with sustainDurationMs.
   * After the duration elapses, stops all sustain VFX and fires expiration steps.
   */
  private scheduleSustainCleanup(skillId: string): void {
    const durationMs = this.sustainDurations.get(skillId);
    if (durationMs == null) return;

    // Cancel any existing timer for this skill (re-activation before timer fires)
    this.cancelSustainTimer(skillId);

    const timer = this.scene.time.delayedCall(durationMs, () => {
      this.sustainTimers.delete(skillId);
      this.stopAll(skillId);
      this.fireSteps(skillId, 'expiration');
    });
    this.sustainTimers.set(skillId, timer);
  }

  /**
   * Schedule auto-stop for a sustain primitive played via an external trigger
   * (hit/tick/expiration). Non-sustain primitives are ignored — they self-manage.
   * Each new play resets the timer so the VFX persists from the most recent trigger.
   */
  private scheduleTriggerSustainCleanup(step: VfxStep): void {
    const primitiveType = VfxConfigLoader.getPrimitiveType(step.effectKey);
    if (!SUSTAIN_PRIMITIVES.has(primitiveType)) return;

    const durationMs = step.sustainDurationMs ?? DEFAULT_TRIGGER_SUSTAIN_MS[primitiveType];
    if (durationMs == null || durationMs <= 0) return;

    // Cancel existing timer for this effect key (new trigger resets the timer)
    this.cancelTriggerSustainTimer(step.effectKey);

    const timer = this.scene.time.delayedCall(durationMs, () => {
      this.triggerSustainTimers.delete(step.effectKey);
      const primitive = this.factory.get(step.effectKey);
      primitive.stop();
      this.activeSustainKeys.delete(step.effectKey);
    });
    this.triggerSustainTimers.set(step.effectKey, timer);
  }

  /** Cancel a pending trigger sustain timer for a specific effect key. */
  private cancelTriggerSustainTimer(effectKey: string): void {
    const existing = this.triggerSustainTimers.get(effectKey);
    if (existing) {
      existing.remove();
      this.triggerSustainTimers.delete(effectKey);
    }
  }

  /** Track a pending delayed call for a skill so it can be cancelled on deactivation. */
  private addDelayedCall(skillId: string, timer: Phaser.Time.TimerEvent): void {
    let list = this.pendingDelayedCalls.get(skillId);
    if (!list) {
      list = [];
      this.pendingDelayedCalls.set(skillId, list);
    }
    list.push(timer);
  }

  /** Remove a fired delayed call from tracking. */
  private removeDelayedCall(skillId: string, timer: Phaser.Time.TimerEvent): void {
    const list = this.pendingDelayedCalls.get(skillId);
    if (!list) return;
    const idx = list.indexOf(timer);
    if (idx >= 0) list.splice(idx, 1);
    if (list.length === 0) this.pendingDelayedCalls.delete(skillId);
  }

  /** Cancel all pending delayed calls for a skill. */
  private cancelDelayedCalls(skillId: string): void {
    const list = this.pendingDelayedCalls.get(skillId);
    if (!list) return;
    for (const timer of list) timer.remove();
    this.pendingDelayedCalls.delete(skillId);
  }

  /** Cancel a scheduled sustain timer (called on manual deactivation or re-activation). */
  private cancelSustainTimer(skillId: string): void {
    const existing = this.sustainTimers.get(skillId);
    if (existing) {
      existing.remove();
      this.sustainTimers.delete(skillId);
    }
  }

  /** Destroy all VFX primitives — call on scene shutdown. */
  destroy(): void {
    if (this.playerEvents) {
      if (this.onActivated) this.playerEvents.off('skill-activated', this.onActivated);
      if (this.onDeactivated) this.playerEvents.off('skill-deactivated', this.onDeactivated);
      this.playerEvents = null;
    }
    this.onActivated = null;
    this.onDeactivated = null;
    this.getPlayerPosition = null;
    this.getPlayerFacing = null;
    for (const timer of this.sustainTimers.values()) {
      timer.remove();
    }
    this.sustainTimers.clear();
    for (const timer of this.triggerSustainTimers.values()) {
      timer.remove();
    }
    this.triggerSustainTimers.clear();
    for (const list of this.pendingDelayedCalls.values()) {
      for (const timer of list) timer.remove();
    }
    this.pendingDelayedCalls.clear();
    this.factory.destroyAll();
    this.mappingsBySkill.clear();
  }
}
